import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { Box, Typography, Button, IconButton, Stack, Chip } from "@mui/material";
import GenericDataTable from "../components/GenericDataTable";
import ClientModal from "../components/ClientModal";
import ClientContactsManager from "../components/ClientContactsManager";
import { getClients, addClient, updateClient, deleteClient } from "../Services/clientsService";
import { useToast } from "../context/ToastContext";
import { formatDate } from "../utils/date";
import { Person, Visibility } from "@mui/icons-material";

const baseColumns = [
  { field: "nom", headerName: "Nom" },
  { field: "adresse", headerName: "Adresse" },
  { field: "telephone", headerName: "Téléphone" },
  { field: "email", headerName: "Email" },
  { field: "dateCreation", headerName: "Créé le" },
  { field: "actions", headerName: "" },
];

export default function ClientsPage() {
  const [clients, setClients] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [contactsManagerOpen, setContactsManagerOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    getClients()
      .then(setClients)
      .catch(() => {
        setClients([]);
        showToast("Erreur lors du chargement des clients", { severity: 'error' });
      });
  }, []);

  const handleAdd = () => {
    setEditData(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    setEditData(row);
    setModalOpen(true);
  };

  const handleDelete = (row) => {
    deleteClient(row.id)
      .then(() => {
        setClients(clients.filter((c) => c.id !== row.id));
        showToast("Client supprimé", { severity: 'success' });
      })
      .catch(() => showToast("Suppression échouée", { severity: 'error' }));
  };

  const handleSave = (form) => {
    if (editData) {
      updateClient(editData.id, form)
        .then((updated) => {
          setClients(clients.map((c) => (c.id === updated.id ? updated : c)));
          setModalOpen(false);
          showToast("Client mis à jour", { severity: 'success' });
        })
        .catch(() => showToast("Mise à jour échouée", { severity: 'error' }));
    } else {
      addClient(form)
        .then((created) => {
          setClients([...clients, created]);
          setModalOpen(false);
          showToast("Client ajouté", { severity: 'success' });
        })
        .catch(() => showToast("Création échouée", { severity: 'error' }));
    }
  };

  const handleManageContacts = (client) => {
    setSelectedClient(client);
    setContactsManagerOpen(true);
  };

  const handleContactsChange = () => {
    // Recharger les clients si nécessaire
    getClients()
      .then(setClients)
      .catch(() => showToast("Erreur lors du rechargement", { severity: 'error' }));
  };

  const columns = baseColumns.map((c) => {
    if (c.field === 'dateCreation') {
      return { ...c, renderCell: (row) => formatDate(row.dateCreation) };
    }
    if (c.field === 'actions') {
      return {
        ...c,
        renderCell: (row) => (
          <Stack direction="row" spacing={1}>
            <IconButton size="small" onClick={() => handleManageContacts(row)}>
              <Person />
            </IconButton>
          </Stack>
        )
      };
    }
    return c;
  });

  return (
    <div style={{ display: "flex" }}>
      <Sidebar />
      <Box sx={{ flex: 1, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Gestion des clients
        </Typography>
        <Button variant="contained" color="primary" onClick={handleAdd} sx={{ mb: 2 }}>
          Ajouter un client
        </Button>
        <GenericDataTable 
          columns={columns} 
          rows={clients} 
          onEdit={handleEdit} 
          onDelete={handleDelete} 
          bordered
          headerAlign="center"
          cellAlign="center"
          headerSx={{ fontSize: 14, backgroundColor: 'primary.main', color: 'primary.contrastText' }}
          cellSx={{ fontSize: 13 }}
        />
        <ClientModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSave} initialData={editData} />
        <ClientContactsManager
          open={contactsManagerOpen}
          onClose={() => setContactsManagerOpen(false)}
          client={selectedClient}
          onContactsChange={handleContactsChange}
        />
      </Box>
    </div>
  );
} 